"use client";

import type { ComponentType } from "react";
import {
  BadmintonDisplay,
  BasketballDisplay,
  CricketDisplay,
  CyclingDisplay,
  FootballDisplay,
  PadelDisplay,
  PickleballDisplay,
  RunningDisplay,
  SnookerDisplay,
  SquashDisplay,
  SwimmingDisplay,
  TableTennisDisplay,
  TennisDisplay,
  ThrowballDisplay,
  VolleyballDisplay,
  WalkingDisplay,
  WeightTrainingDisplay,
} from "@/components/posts/activity-templates";
import { AnimalFlowDisplay } from "@/components/posts/activity-templates/animal-flow-template";
import { BoulderingDisplay } from "@/components/posts/activity-templates/bouldering-template";
import { ClimbingDisplay } from "@/components/posts/activity-templates/climbing-template";
import { DayRetreatsDisplay } from "@/components/posts/activity-templates/day-retreats-template";
import { GolfDisplay } from "@/components/posts/activity-templates/golf-template";
import { HIITDisplay } from "@/components/posts/activity-templates/hiit-template";
import { HotYogaDisplay } from "@/components/posts/activity-templates/hot-yoga-template";
import { HyroxDisplay } from "@/components/posts/activity-templates/hyrox-template";
import { MixedMartialArtsDisplay } from "@/components/posts/activity-templates/mixed-martial-arts-template";
import { NutritionDisplay } from "@/components/posts/activity-templates/nutrition-template";
import { PilatesDisplay } from "@/components/posts/activity-templates/pilates-template";
import { RecoveryDisplay } from "@/components/posts/activity-templates/recovery-template";
import { ScubaDivingDisplay } from "@/components/posts/activity-templates/scuba-diving-template";
import { SnorkelingDisplay } from "@/components/posts/activity-templates/snorkeling-template";
import { StretchingDisplay } from "@/components/posts/activity-templates/stretching-template";
import { TrekkingDisplay } from "@/components/posts/activity-templates/trekking-template";
import { YogaDisplay } from "@/components/posts/activity-templates/yoga-template";
import type { ActivitySessionFormat } from "@/db/schema/activity-session";
import type { ActivitySeedId } from "@/db/seed-data/activities";
import { getActivityRendererFamily } from "@/lib/activity-rules";

export type ActivityPreviewParticipant = {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  username: string | null;
};

type ActivityPreviewComponent = ComponentType<{
  data: unknown;
  participants: ActivityPreviewParticipant[];
}>;

const PREVIEW_COMPONENTS: Partial<
  Record<ActivitySeedId, ActivityPreviewComponent>
> = {
  badminton: BadmintonDisplay as ActivityPreviewComponent,
  basketball: BasketballDisplay as ActivityPreviewComponent,
  climbing: ClimbingDisplay as ActivityPreviewComponent,
  cricket: CricketDisplay as ActivityPreviewComponent,
  cycling: CyclingDisplay as ActivityPreviewComponent,
  "day-retreats": DayRetreatsDisplay as ActivityPreviewComponent,
  football: FootballDisplay as ActivityPreviewComponent,
  golf: GolfDisplay as ActivityPreviewComponent,
  hyrox: HyroxDisplay as ActivityPreviewComponent,
  "mixed-martial-arts": MixedMartialArtsDisplay as ActivityPreviewComponent,
  padel: PadelDisplay as ActivityPreviewComponent,
  pickleball: PickleballDisplay as ActivityPreviewComponent,
  pilates: PilatesDisplay as ActivityPreviewComponent,
  recovery: RecoveryDisplay as ActivityPreviewComponent,
  running: RunningDisplay as ActivityPreviewComponent,
  snooker: SnookerDisplay as ActivityPreviewComponent,
  squash: SquashDisplay as ActivityPreviewComponent,
  swimming: SwimmingDisplay as ActivityPreviewComponent,
  "table-tennis": TableTennisDisplay as ActivityPreviewComponent,
  tennis: TennisDisplay as ActivityPreviewComponent,
  throwball: ThrowballDisplay as ActivityPreviewComponent,
  volleyball: VolleyballDisplay as ActivityPreviewComponent,
  walking: WalkingDisplay as ActivityPreviewComponent,
  "weight-training": WeightTrainingDisplay as ActivityPreviewComponent,
  yoga: YogaDisplay as ActivityPreviewComponent,
  hiit: HIITDisplay as ActivityPreviewComponent,
  "hot-yoga": HotYogaDisplay as ActivityPreviewComponent,
  nutrition: NutritionDisplay as ActivityPreviewComponent,
  "scuba-diving": ScubaDivingDisplay as ActivityPreviewComponent,
  snorkeling: SnorkelingDisplay as ActivityPreviewComponent,
  trekking: TrekkingDisplay as ActivityPreviewComponent,
  "animal-flow": AnimalFlowDisplay as ActivityPreviewComponent,
  bouldering: BoulderingDisplay as ActivityPreviewComponent,
  stretching: StretchingDisplay as ActivityPreviewComponent,
};

const PREVIEW_COMPONENT_ENTRIES = Object.entries(PREVIEW_COMPONENTS) as Array<
  [ActivitySeedId, ActivityPreviewComponent]
>;

const SCORE_EDITOR_ACTIVITY_IDS = new Set([
  "tennis",
  "badminton",
  "tabletennis",
  "squash",
  "padel",
  "pickleball",
  "snooker",
  "basketball",
  "football",
  "volleyball",
  "throwball",
  "cricket",
]);

const normalizeActivityId = (activityId: string) =>
  activityId
    .toLowerCase()
    .replace(/[^a-z-]/g, "")
    .replace(/-/g, "");

export function getActivityPreviewComponent(
  activityId: string,
): ActivityPreviewComponent | null {
  const normalizedId = normalizeActivityId(activityId);
  const entry = PREVIEW_COMPONENT_ENTRIES.find(
    ([key]) => normalizeActivityId(key) === normalizedId,
  );

  return entry?.[1] ?? null;
}

export function isActivityPreviewSupported(activityId: string): boolean {
  return getActivityPreviewComponent(activityId) != null;
}

export function shouldUseActivitySessionPreview(
  activityId: string,
  sessionFormat: ActivitySessionFormat | null,
): boolean {
  if (!isActivityPreviewSupported(activityId)) {
    return false;
  }

  const family = getActivityRendererFamily(activityId);
  if (family === "group_session") {
    return true;
  }

  return sessionFormat != null && sessionFormat !== "group";
}

export function supportsActivityScoreEditor(activityId: string): boolean {
  return SCORE_EDITOR_ACTIVITY_IDS.has(normalizeActivityId(activityId));
}
